import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { theme } from "../../themes/theme";
import ProgressPieChart from "../../components/TableItems/ProgressPieChart";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../redux/store";
import { displayUserRole } from "../../utils/StringUtils";
import { RoleState } from "../../types/models/RoleState";
import { TeamMembersState } from "../../types/models/TeamMembersState";
import { UserFitnessStatus } from "../../types/models/UserFitnessStatus";
import { UserSkill } from "../../types/models/UserSkillCoverage";
import { getMyFitForWork, getMyTraining } from "../../redux/actions";
import { fontColor } from "../../constants/ColorSets";
import { QuestionMark } from "@mui/icons-material";
import MemberModal from "../your-teams/MemberModal.tsx";
import { RuleUnitState } from "../../types/models/RuleUnitState";
import { TeamMember, TeamMemberProgress } from "../../types/models/TeamMember";
import { getAuthUserInfoFromToken } from "../../components/AppBase/AppAuthProvider";
import { useAlerts } from "../../hooks/useAlerts";
import { getFitForWorkIcon } from "../../utils/FitForWorkUtil";
import { useTeams } from "../../hooks/useTeams";
import ProgressPieNumber from "../../components/TableItems/ProgressPieNumber";

const UserTable: React.FC = () => {
  const { filterAlertProgress } = useTeams();

  const [openUserProgress, setOpenUserProgress] = useState<boolean>(false);
  const [myTraining, setMyTraining] = useState<UserSkill>();
  const [isTrainingLoading, setIsTrainingLoading] = useState<boolean>(false);
  const [myFitForWork, setMyFitForWork] = useState<UserFitnessStatus>();
  const [isFitForWorkLoading, setIsFitForWorkLoading] = useState<boolean>(false);

  const dispatch = useDispatch();
  const teamState = useSelector<AppState, TeamMembersState>((state) => state.teamMembers);
  const roleState = useSelector<AppState, RoleState>((state) => state.role);
  const ruleState = useSelector<AppState, RuleUnitState>((state) => state.ruleUnit);
  const { refreshAlerts } = useAlerts();
  const userInfo = getAuthUserInfoFromToken();

  useEffect(() => {
    setIsTrainingLoading(true);
    setIsFitForWorkLoading(true);
    dispatch(
      getMyTraining((training) => {
        setMyTraining(training);
        setIsTrainingLoading(false);
        dispatch(
          getMyFitForWork((fitness) => {
            setMyFitForWork(fitness);
            setIsFitForWorkLoading(false);
          })
        );
      })
    );
    // Gaining Alerts when reloaded on the user-profile page
    if (!ruleState.baseAlertContentRevisions) {
      refreshAlerts();
    }
  }, []);

  const getPieChartForUserProgress = () => {
    const userProgress = teamState.myProgress;
    const total =
      userProgress?.alertCount && userProgress.acknowledgedCount
        ? userProgress.alertCount - userProgress?.acknowledgedCount
        : 0;
    return (
      <Box sx={{ position: "relative" }}>
        <ProgressPieChart
          total={userProgress?.alertCount ?? 0}
          highlighted={userProgress?.overdueCount ?? 0}
          completed={userProgress?.acknowledgedCount ?? 0}
          modal={false}
        />
        {total === 0 ? null : total > 0 && <ProgressPieNumber num={total} />}
      </Box>
    );
  };

  const createTrainingPieChart = () => {
    if (isTrainingLoading) {
      return <CircularProgress size="2rem" sx={{ color: fontColor.orangeTitle }} />;
    } else {
      if (myTraining && myTraining.Total !== null && myTraining.Total !== 0) {
        return (
          <ProgressPieChart
            completed={myTraining.Valid ?? 0}
            total={myTraining.Total ?? 0}
            highlighted={(myTraining.Total ?? 0) - (myTraining.Valid ?? 0)}
            modal={false}
          ></ProgressPieChart>
        );
      } else {
        return <QuestionMark sx={{ color: "lightgray", width: "32px", height: "46px" }} />;
      }
    }
  };

  const createFitForWorkIcon = () => {
    if (isFitForWorkLoading) {
      return <CircularProgress size="2rem" sx={{ ml: 2, color: fontColor.orangeTitle }} />;
    } else {
      if (myFitForWork && myFitForWork.StatusKey !== null) {
        const icon = getFitForWorkIcon(myFitForWork.StatusKey, "46px");
        return (
          <Box sx={{ ml: 2 }}>
            <Tooltip title={myFitForWork.Status}>{icon}</Tooltip>
          </Box>
        );
      } else {
        return <QuestionMark sx={{ color: "lightgray", width: "32px", height: "46px", ml: 3 }} />;
      }
    }
  };

  const convertToTeamMember = useMemo(() => {
    const member: TeamMember = {
      name: userInfo?.Name ?? "", // Populate name from token
      email: teamState.myProgress?.email ?? "",
      locations: teamState.myProgress?.locations ?? [],
      roles: teamState.myProgress?.roles ?? [],
      user_id: teamState.myProgress?.user_id ?? "",
      roleBundles: teamState.myProgress?.roleBundles ?? [],
    };
    return member;
  }, [teamState.myProgress]);

  const convertToTeamMemberProgress = useMemo(() => {
    const memberProgress: TeamMemberProgress = {
      name: teamState.myProgress?.name ?? "",
      email: teamState.myProgress?.email ?? "",
      locations: teamState.myProgress?.locations ?? [],
      roles: teamState.myProgress?.roles ?? [],
      user_id: teamState.myProgress?.user_id ?? "",
      acknowledgedCount: teamState.myProgress?.acknowledgedCount ?? 0,
      alertCount: teamState.myProgress?.alertCount ?? 0,
      overdueCount: teamState.myProgress?.overdueCount ?? 0,
      progress: null,
      unreadMessage: teamState.myProgress?.unreadMessage ?? false,
      roleBundles: teamState.myProgress?.roleBundles ?? [],
    };
    return memberProgress;
  }, [teamState.myProgress]);

  return (
    <>
      <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: theme.table.subHeaderBgColor }}>
            <TableRow>
              <TableCell>Role Bundles</TableCell>
              <TableCell>Roles</TableCell>
              <TableCell width={"100px"}>Alerts</TableCell>
              <TableCell width={"100px"}>Training</TableCell>
              <TableCell width={"140px"}>Fitness</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow sx={{ cursor: "pointer" }} onClick={() => setOpenUserProgress(true)}>
              <TableCell>
                {teamState.myProgress?.roleBundles &&
                teamState.myProgress.roleBundles.length > 0 ? (
                  teamState.myProgress?.roleBundles?.map((bundle) => bundle.name).join(", ")
                ) : (
                  <Typography sx={{ fontSize: 13, color: theme.table.subTextColor }}>
                    No role bundles assigned
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                {roleState.myRoles
                  ?.map((role) => displayUserRole(role.RoleId, roleState.knownRoles?.roles))
                  .join(", ")}
              </TableCell>
              <TableCell align="center">{getPieChartForUserProgress()}</TableCell>
              <TableCell align="center">{createTrainingPieChart()}</TableCell>
              <TableCell align="left">{createFitForWorkIcon()} </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      {teamState.myProgress && (
        <MemberModal
          open={openUserProgress}
          onClose={() => setOpenUserProgress(false)}
          currentTeam={undefined}
          alerts={ruleState.baseAlertContentRevisionDetails
            ?.map((baseAlert) =>
              baseAlert.RuleInstances?.map((rule) => ({
                Metadata: rule.Metadata,
                RuleInstanceId: rule.RuleInstanceId,
              }))
            )
            .flat()}
          selectedMember={convertToTeamMemberProgress}
          user={convertToTeamMember}
          userProgress={filterAlertProgress(
            teamState?.userProgress?.progress ?? [],
            teamState?.userProgress?.roles ?? [],
            teamState?.userProgress?.locations ?? []
          )}
          userTraining={myTraining}
          userFitness={myFitForWork}
          userProfilePage
        />
      )}
    </>
  );
};

export default UserTable;
